import { useContext, useEffect, useMemo, useRef } from 'react'

import { ObservatoryContext } from '@/feature/observatory/contexts/ObservatoryContext'
import { useGlobalOrgTags, useTagsStore } from '@/feature/tags'

import { Asset } from '@/lib/features/Assets/interfaces'

import { resolveAssetTags } from './AssetTagsTab.utils'

/**
 * Reconciles the asset's tags against `globalOrgTags`, which `useTagEdit` and `useTagDelete` write
 * to directly, so a tag renamed or deleted from the popout updates here without a refetch. Tags no
 * longer in that list are dropped rather than rendered stale.
 *
 * The assignment itself is read back from the assets list like everywhere else: the popout holds the
 * optimistic state, and the list is revalidated once the request settles. The map page has no
 * page-level revalidation of its own, so it is triggered here — `pages/assets/[table].tsx` does the
 * same off the same `isTagsUpdating` transition.
 */
export const useAssetTags = (asset: Asset | undefined) => {
  const { globalOrgTags, isLoading } = useGlobalOrgTags()
  const { assets } = useContext(ObservatoryContext)

  const isTagsUpdating = useTagsStore((state) => state.isTagsUpdating)
  const wasTagsUpdating = useRef(false)

  useEffect(() => {
    if (wasTagsUpdating.current && !isTagsUpdating) {
      assets.mutate()
    }
    wasTagsUpdating.current = isTagsUpdating
  }, [isTagsUpdating, assets])

  const tags = useMemo(
    () => resolveAssetTags(asset?.tags, globalOrgTags),
    [asset?.tags, globalOrgTags],
  )

  return { tags, isLoading }
}
